import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { CentrexExtensionRuleModel } from '../../models/centrex-extension-rule.model';
import { CentrexExtensionRulesService } from '../../services/centrex-extension-rules.service';
import { RedirectionsFormComponent } from './redirections-form/redirections-form.component';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { CentrexCalendarsService } from 'src/app/services/centrex-calendars.service';
import { forkJoin } from 'rxjs';
import { OrganizationsService } from 'src/app/services/organization.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { PlatformService } from 'src/app/services/platform.service';

@Component({
  selector: 'app-redirections',
  templateUrl: './redirections.component.html',
  styleUrls: ['./redirections.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTableModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatPaginator,
    NgClass,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    NgxSpinnerModule,
  ],
})
export class RedirectionsComponent implements OnInit {
  installationDataSource = new MatTableDataSource<CentrexExtensionRuleModel>();
  extensionDataSource = new MatTableDataSource<CentrexExtensionRuleModel>();

  filteredInstallationDataSource =
    new MatTableDataSource<CentrexExtensionRuleModel>();

  filteredExtensionDataSource =
    new MatTableDataSource<CentrexExtensionRuleModel>();

  displayedColumns: string[] = [
    'id',
    'assignedTo',
    'active',
    'type',
    //'filter',
    'target',
    'calendar',
    'ringSeconds',
    'actions',
  ];

  installationFilters = {
    id: '',
    assignedTo: '',
    active: '',
    type: '',
    target: '',
    calendar: '',
  };

  extensionFilters = {
    id: '',
    assignedTo: '',
    active: '',
    type: '',
    target: '',
    calendar: '',
  };

  platforms: string[] = ['Centrex NG', 'Centrex Enreach'];
  platform = this.platformService.getCurrentPlatform();
  private calendarMap: { [id: string]: string } = {};

  organizationId: string | null = null;

  private spinnerService = inject(NgxSpinnerService);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private rulesService: CentrexExtensionRulesService,
    private dialog: MatDialog,
    private calendarsService: CentrexCalendarsService,
    private organizationsService: OrganizationsService,
    private platformService: PlatformService,
  ) {
    console.log('[REDIRECTIONS] constructor');
  }
  ngOnInit(): void {
    console.log('[REDIRECTIONS] ngOnInit');

    // Écoute changements d'organisation
    this.organizationsService.currentOrganizationChange$.subscribe((org) => {
      console.log('[REDIRECTIONS] org change', org?.id);

      if (org) {
        this.organizationId = org.id;
        this.fetchRules();
      }
    });
  }

  private enrichRules(
    rules: CentrexExtensionRuleModel[] | null | undefined,
  ): (CentrexExtensionRuleModel & { name: string; calendarDisplay: string })[] {
    if (!Array.isArray(rules)) return [];
    return rules.map((rule) => ({
      ...rule,
      name: `${rule.extensionId}`,
      calendarDisplay: this.calendarMap[rule.calendarId] ?? '',
    }));
  }

  fetchRules(): void {
    if (!this.organizationId) {
      return;
    }

    this.spinnerService.show('spinner');

    forkJoin({
      rules: this.rulesService.getExtensionRules(
        this.organizationId,
        this.platform,
      ),
      calendars: this.calendarsService.getCalendars(
        this.organizationId,
        this.platform,
      ),
    }).subscribe({
      next: ({ rules, calendars }) => {
        this.calendarMap = (calendars ?? []).reduce(
          (acc, calendar) => {
            acc[calendar.id] = calendar.name;
            return acc;
          },
          {} as { [id: string]: string },
        );

        const enriched = this.enrichRules(rules);

        // 1️⃣ Règles d’installation
        const installationRules = enriched.filter(
          (r) => r.extensionId === 'Enterprise',
        );

        // 2️⃣ Règles par extension
        const extensionRules = enriched.filter(
          (r) => r.extensionId !== 'Enterprise',
        );

        this.installationDataSource.data = installationRules;
        this.extensionDataSource.data = extensionRules;

        this.filteredInstallationDataSource.data = installationRules;
        this.filteredExtensionDataSource.data = extensionRules;

        this.filteredInstallationDataSource.sort = this.sort;
        this.filteredInstallationDataSource.paginator = this.paginator;

        this.filteredExtensionDataSource.sort = this.sort;
        this.filteredExtensionDataSource.paginator = this.paginator;

        this.spinnerService.hide('spinner');
      },
      error: (err) => {
        console.error(
          'Erreur lors du chargement des règles ou calendriers',
          err,
        );
        Promise.resolve().then(() => {
          this.spinnerService.hide('spinner');
        });
      },
    });
  }

  applyExtensionFilters(): void {
    const { id, assignedTo, active, type, target, calendar } =
      this.extensionFilters;

    this.filteredExtensionDataSource.data =
      this.extensionDataSource.data.filter((rule) => {
        return (
          (!id || rule.id?.toLowerCase().includes(id.toLowerCase())) &&
          (!assignedTo ||
            rule.extensionId
              ?.toLowerCase()
              .includes(assignedTo.toLowerCase())) &&
          (!active ||
            (rule.enabled ? 'oui' : 'non').includes(active.toLowerCase())) &&
          (!type ||
            rule.ringState?.toLowerCase().includes(type.toLowerCase())) &&
          (!target ||
            rule.target?.toLowerCase().includes(target.toLowerCase())) &&
          (!calendar ||
            rule.calendarId?.toLowerCase().includes(calendar.toLowerCase()) ||
            (!rule.calendarId && 'toujours'.includes(calendar.toLowerCase())))
        );
      });
  }

  applyInstallationFilters(): void {
    const { id, assignedTo, active, type, target, calendar } =
      this.installationFilters;

    this.filteredInstallationDataSource.data =
      this.installationDataSource.data.filter((rule) => {
        return (
          (!id || rule.id?.toLowerCase().includes(id.toLowerCase())) &&
          (!assignedTo ||
            rule.extensionId
              ?.toLowerCase()
              .includes(assignedTo.toLowerCase())) &&
          (!active ||
            (rule.enabled ? 'oui' : 'non').includes(active.toLowerCase())) &&
          (!type ||
            rule.ringState?.toLowerCase().includes(type.toLowerCase())) &&
          (!target ||
            rule.target?.toLowerCase().includes(target.toLowerCase())) &&
          (!calendar ||
            rule.calendarId?.toLowerCase().includes(calendar.toLowerCase()) ||
            (!rule.calendarId && 'toujours'.includes(calendar.toLowerCase())))
        );
      });
  }

  onPlatformChange(): void {
    this.platformService.setPlatform(this.platform);
    this.fetchRules();
  }

  onView(rule: CentrexExtensionRuleModel): void {
    this.dialog.open(RedirectionsFormComponent, {
      width: '700px',
      data: { rule, mode: 'view' },
    });
  }
}
